import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import { apiFetch } from '../lib/api';
import type { ClubFlight, ClubHome } from '../lib/club';
import { formatFlightTime, formatIsoDate } from '../lib/format';
import { toFaDigits } from '../lib/jalali';
import { ClubLanding } from './ClubLanding';
import { ClubProfileForm } from './ClubProfileForm';

function formatPhone(phone: string | undefined, isFa: boolean): string {
  if (!phone) return '';
  const match = /^\+98(9\d{2})(\d{3})(\d{4})$/.exec(phone);
  const text = match ? `+98 ${match[1]} ${match[2]} ${match[3]}` : phone;
  return isFa ? toFaDigits(text) : text;
}

function when(value: string, isFa: boolean, locale: string): string {
  if (!value) return '';
  const date = formatIsoDate(value, isFa);
  const time = value.includes(' ')
    ? value.slice(11, 16)
    : value.includes('T')
      ? formatFlightTime(value, locale)
      : '';
  return time ? `${date} · ${isFa ? toFaDigits(time) : time}` : date;
}

export function MilesPage() {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const loggedIn = Boolean(localStorage.getItem('sa_access_token'));
  const [data, setData] = useState<ClubHome | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!loggedIn) return;
    let cancelled = false;
    void apiFetch<ClubHome>('/api/v1/ffp/me')
      .then((home) => {
        if (!cancelled) setData(home);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [loggedIn]);

  if (!loggedIn) {
    return <ClubLanding />;
  }

  const num = (value: number) => {
    const text = value.toLocaleString('en-US');
    return isFa ? toFaDigits(text) : text;
  };
  const profile = data?.profile;
  const displayName = profile
    ? [profile.localFirstName, profile.localLastName].filter(Boolean).join(' ') ||
      `${profile.firstName} ${profile.lastName}`
    : '';

  return (
    <main className="sa-page">
      <div className="sa-container club-page">
        <header className="sa-page__header">
          <p className="home-section__eyebrow">{t('club.eyebrow')}</p>
          <h1 className="sa-headline">{t('club.title')}</h1>
          <p className="sa-text-muted">{t('club.lead')}</p>
        </header>

        {error ? <div className="sa-alert sa-alert--error">{error}</div> : null}

        {data ? (
          <div className="club-layout">
            <section className="club-card">
              <div className="club-card__top">
                <div>
                  <p className="club-card__kicker">{t('club.member')}</p>
                  <h2>{displayName}</h2>
                  <p className="club-card__number" dir="ltr">
                    {data.memberNumber}
                  </p>
                </div>
                <span className="club-card__tier">{data.tier}</span>
              </div>
              <div>
                <p className="club-card__kicker">{t('club.awardMiles')}</p>
                <p className="club-card__miles">{num(data.balance)}</p>
              </div>
              <dl className="club-stats">
                <div>
                  <dt>{t('club.lifetime')}</dt>
                  <dd>{num(data.lifetimeMiles)}</dd>
                </div>
                <div>
                  <dt>{t('club.statusMiles')}</dt>
                  <dd>{num(data.statusMiles || data.lifetimeMiles)}</dd>
                </div>
                <div>
                  <dt>{t('club.segments')}</dt>
                  <dd>{num(data.segments)}</dd>
                </div>
              </dl>
              <div className="club-progress" aria-hidden="true">
                <span style={{ width: `${Math.round(data.progress * 100)}%` }} />
              </div>
              <p className="club-card__next">
                {data.nextTier
                  ? t('club.milesToNext', { miles: num(data.milesToNextTier), tier: data.nextTier })
                  : t('club.topTier')}
              </p>
              <p className="club-card__benefit">
                {t(`club.benefits.${data.tier}`, { defaultValue: t('club.benefits.BLUE') })}
              </p>
              {data.tierExpiresAt ? (
                <p className="club-card__until">
                  {t('club.tierUntil', { date: formatIsoDate(data.tierExpiresAt, isFa) })}
                </p>
              ) : null}
            </section>

            {!data.profileComplete && !editing ? (
              <div className="club-banner">
                <div>
                  <strong>{t('club.completeTitle')}</strong>
                  <p>{t('club.completeLead')}</p>
                </div>
                <Button type="button" onClick={() => setEditing(true)}>
                  {t('club.completeAction')}
                </Button>
              </div>
            ) : null}

            <section className="club-panel">
              <div className="club-panel__head">
                <h2>{t('club.profile')}</h2>
                {!editing ? (
                  <button type="button" className="club-text-btn" onClick={() => setEditing(true)}>
                    {t('club.edit')}
                  </button>
                ) : null}
              </div>
              {editing ? (
                <ClubProfileForm
                  home={data}
                  onCancel={() => setEditing(false)}
                  onSaved={(next) => {
                    setData(next);
                    setEditing(false);
                  }}
                />
              ) : (
                <ProfileFacts home={data} isFa={isFa} />
              )}
            </section>

            <section className="club-panel">
              <h2>{t('club.upcoming')}</h2>
              <FlightList flights={data.flights.upcoming} isFa={isFa} locale={i18n.language} empty={t('club.emptyFlights')} />
              <h2 className="club-panel__sub">{t('club.past')}</h2>
              <FlightList flights={data.flights.past} isFa={isFa} locale={i18n.language} empty={t('club.emptyFlights')} />
            </section>

            <section className="club-panel club-span">
              <h2>{t('club.activity')}</h2>
              {data.activity.length === 0 ? (
                <p className="sa-text-muted">{t('club.emptyActivity')}</p>
              ) : (
                <ul className="club-activity">
                  {data.activity.map((row) => (
                    <li key={row.id}>
                      <div>
                        <strong>{t(`club.reasons.${row.reason}`, { defaultValue: row.reason })}</strong>
                        <p className="sa-text-muted">
                          {[row.flightNumber, row.route, row.pnr].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <div className="club-activity__meta">
                        <span className={row.delta >= 0 ? 'is-credit' : 'is-debit'}>
                          {row.delta >= 0 ? '+' : ''}
                          {num(row.delta)}
                        </span>
                        <time dateTime={row.createdAt}>{formatIsoDate(row.createdAt, isFa)}</time>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : !error ? (
          <p className="sa-text-muted">{t('common.loading')}</p>
        ) : null}
      </div>
    </main>
  );
}

function ProfileFacts({ home, isFa }: { home: ClubHome; isFa: boolean }) {
  const { t } = useTranslation();
  const profile = home.profile;
  const rows: { label: string; value?: string }[] = [
    { label: t('common.fullName'), value: `${profile.firstName} ${profile.lastName}` },
    {
      label: t('club.localFirstName'),
      value: [profile.localFirstName, profile.localLastName].filter(Boolean).join(' '),
    },
    { label: t('common.birthDate'), value: profile.birthDate ? formatIsoDate(profile.birthDate, isFa) : undefined },
    { label: t('common.nationality'), value: profile.nationality },
    { label: t('common.nationalCode'), value: profile.nationalId },
    { label: t('common.passportNumber'), value: profile.passportNumber },
    { label: t('common.email'), value: home.contact.email },
    { label: t('common.mobile'), value: formatPhone(home.contact.phone, isFa) },
    { label: t('club.seat'), value: profile.seatPreference ? t(`club.seats.${profile.seatPreference}`) : undefined },
    { label: t('club.meal'), value: profile.mealPreference ? t(`club.meals.${profile.mealPreference}`) : undefined },
    { label: t('club.homeAirport'), value: profile.homeAirport },
  ];
  return (
    <dl className="club-facts">
      {rows
        .filter((row) => row.value)
        .map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd dir="auto">{row.value}</dd>
          </div>
        ))}
    </dl>
  );
}

function FlightList({
  flights,
  isFa,
  locale,
  empty,
}: {
  flights: ClubFlight[];
  isFa: boolean;
  locale: string;
  empty: string;
}) {
  const { t } = useTranslation();
  if (flights.length === 0) return <p className="sa-text-muted">{empty}</p>;
  return (
    <ul className="club-flights">
      {flights.map((flight) => (
        <li key={flight.id}>
          <div>
            <strong dir="ltr">
              {flight.origin} → {flight.destination}
            </strong>
            <p className="sa-text-muted">
              {[flight.flightNumber, when(flight.departureAt, isFa, locale), flight.pnr]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <div className="club-flights__meta">
            <span>{t(`club.status.${flight.status}`, { defaultValue: flight.status })}</span>
            <span>{flight.miles === null ? t('club.noMiles') : `+${isFa ? toFaDigits(flight.miles) : flight.miles}`}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
